import { useEffect, useRef, useState } from "react";

/**
 * Props:
 *  - url (required)
 *  - autoLoadOnView? (bool) : if true, mount embed when scrolled near viewport
 */
export default function LiteInstagram({ url, autoLoadOnView = false }) {
  const [show, setShow] = useState(false);
  const rootRef = useRef(null);

  // Auto-load on scroll into view (optional)
  useEffect(() => {
    if (!autoLoadOnView || show === true || !rootRef.current) return;
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setShow(true);
          io.disconnect();
        }
      },
      { root: null, rootMargin: "200px", threshold: 0.01 }
    );
    io.observe(rootRef.current);
    return () => io.disconnect();
  }, [autoLoadOnView, show]);

  // When showing, ensure the IG script is present and process embeds
  useEffect(() => {
    if (!show) return;
    const existing = document.querySelector('script[data-ig-embed]');
    if (!existing) {
      const s = document.createElement("script");
      s.src = "https://www.instagram.com/embed.js";
      s.async = true;
      s.defer = true;
      s.setAttribute("data-ig-embed", "1");
      document.body.appendChild(s);
      s.onload = () => window.instgrm?.Embeds?.process?.();
    } else {
      window.instgrm?.Embeds?.process?.();
    }
  }, [show]);

  return (
    <div ref={rootRef} className="card overflow-hidden">
      {!show ? (
        <button
          type="button"
          onClick={() => setShow(true)}
          className="relative block w-full"
          aria-label="View Instagram post"
        >
          {/* prettier placeholder */}
          <div className="aspect-[4/5] rounded-xl overflow-hidden">
            <div className="h-full w-full bg-gradient-to-br from-pink-500 via-red-500 to-yellow-500 opacity-30" />
            <div className="absolute inset-0 grid place-items-center">
              <div className="flex flex-col items-center gap-2">
                <IgIcon className="h-10 w-10 text-white/80" />
                <span className="text-white/90 text-sm">View Instagram post</span>
                {!autoLoadOnView && <span className="text-white/60 text-xs">Loads on tap</span>}
                {autoLoadOnView && <span className="text-white/60 text-xs">Loads when visible</span>}
              </div>
            </div>
          </div>
        </button>
      ) : (
        <div className="ig-embed">
          <blockquote
            className="instagram-media"
            data-instgrm-permalink={url}
            data-instgrm-version="14"
            style={{ margin: 0 }}
          />
        </div>
      )}
    </div>
  );
}

function IgIcon({ className = "h-6 w-6" }) {
  return (
    <svg viewBox="0 0 24 24" className={className} fill="currentColor" aria-hidden="true">
      <path d="M7 2h10a5 5 0 015 5v10a5 5 0 01-5 5H7a5 5 0 01-5-5V7a5 5 0 015-5zm0 2a3 3 0 00-3 3v10a3 3 0 003 3h10a3 3 0 003-3V7a3 3 0 00-3-3H7zm5 3a5 5 0 110 10 5 5 0 010-10zm0 2.2a2.8 2.8 0 100 5.6 2.8 2.8 0 000-5.6zM18 6.5a1 1 0 11-2 0 1 1 0 012 0z" />
    </svg>
  );
}
